<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const features = [
  {
    title: 'High Performance',
    description: 'Lightning-fast cloud infrastructure designed for maximum efficiency',
    icon: 'fas fa-bolt'
  },
  {
    title: 'Global Network',
    description: 'Strategically located data centers for optimal coverage',
    icon: 'fas fa-globe'
  },
  {
    title: 'Security First',
    description: 'Enterprise-grade security with advanced threat protection',
    icon: 'fas fa-shield-alt'
  },
  {
    title: '24/7 Support',
    description: 'Round-the-clock expert support whenever you need it',
    icon: 'fas fa-headset'
  }
]

const terminalLines = ref<string[]>([])
const currentLine = ref('')
const commands = [
  'syniac init my-app',
  'Initializing cloud infrastructure...',
  'Setting up compute resources...',
  'Configuring network...',
  'Deploying application...',
  'Setup complete! Your app is live at https://my-app.syniac.com'
]

const typeCommand = async (command: string, index: number) => {
  currentLine.value = ''
  for (const char of command) {
    currentLine.value += char
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  terminalLines.value.push(currentLine.value)
  currentLine.value = ''
  
  if (index < commands.length - 1) {
    setTimeout(() => typeCommand(commands[index + 1], index + 1), 1000)
  }
}

onMounted(() => {
  setTimeout(() => typeCommand(commands[0], 0), 1000)
})

const stats = [
  { label: 'Global Data Centers', value: '11' },
  { label: 'Enterprise Customers', value: '35,000+' },
  { label: 'Uptime SLA', value: '99.99%' },
  { label: 'Countries Served', value: '150+' }
]

const performanceData = {
  labels: ['Traditional', 'Syniac'],
  datasets: [
    {
      label: 'Response Time (ms)',
      data: [36, 17],
      backgroundColor: ['#475569', '#0ea5e9'],
      borderColor: ['#475569', '#0ea5e9'],
      borderWidth: 1
    },
    {
      label: 'Resource Utilization (%)',
      data: [85, 40],
      backgroundColor: ['#475569', '#0ea5e9'],
      borderColor: ['#475569', '#0ea5e9'],
      borderWidth: 1
    },
    {
      label: 'Cost Savings (%)',
      data: [0, 65],
      backgroundColor: ['#475569', '#0ea5e9'],
      borderColor: ['#475569', '#0ea5e9'],
      borderWidth: 1
    }
  ]
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: '#374151'
      },
      ticks: {
        color: '#9CA3AF'
      }
    },
    x: {
      grid: {
        color: '#374151'
      },
      ticks: {
        color: '#9CA3AF'
      }
    }
  },
  plugins: {
    legend: {
      labels: {
        color: '#9CA3AF'
      }
    }
  }
}

const techStack = [
  {
    category: 'Compute',
    items: [
      { name: 'AMD EPYC', description: 'Latest generation processors', icon: 'fas fa-microchip' },
      { name: 'NVIDIA A100', description: 'Enterprise GPU computing', icon: 'fas fa-desktop' },
      { name: 'NVMe Storage', description: 'Ultra-fast storage access', icon: 'fas fa-hdd' }
    ]
  },
  {
    category: 'Network',
    items: [
      { name: 'Global Network', description: 'Optimal network locations', icon: 'fas fa-network-wired' },
      { name: 'Global CDN', description: 'Edge content delivery', icon: 'fas fa-globe-americas' },
      { name: 'DDoS Protection', description: 'Advanced threat mitigation', icon: 'fas fa-shield-alt' }
    ]
  },
  {
    category: 'Security',
    items: [
      { name: 'Zero Trust', description: 'Identity-first security', icon: 'fas fa-lock' },
      { name: 'Encryption', description: 'Data protection at rest & in transit', icon: 'fas fa-key' },
      { name: 'ISO 27001', description: 'Certified security controls', icon: 'fas fa-certificate' }
    ]
  }
]
</script>

<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden bg-gray-900">
      <!-- Background Gradient -->
      <div class="absolute inset-0 bg-gradient-to-br from-primary-600/20 via-primary-900/10 to-gray-900"></div>
      
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 class="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white">
              <span class="block">Cloud Computing</span>
              <span class="block mt-2 bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
                for the Future
              </span>
            </h1>
            <p class="mt-6 text-xl text-gray-300">
              Experience the next generation of cloud computing with our cutting-edge infrastructure and innovative solutions.
            </p>
            <div class="mt-8 flex flex-wrap gap-4">
              <RouterLink
                to="/login"
                class="inline-flex items-center px-8 py-3 text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-500 transition-all duration-200 transform hover:scale-105 hover:shadow-lg active:scale-95"
              >
                <span>Get Started</span>
              </RouterLink>
              <RouterLink
                to="/contact"
                class="inline-flex items-center px-8 py-3 text-base font-medium rounded-lg text-primary-400 bg-gray-800 hover:bg-gray-700 transition-all duration-200 transform hover:scale-105 hover:shadow-lg active:scale-95 border border-primary-400/30 hover:border-primary-400"
              >
                <span>Contact Sales</span>
              </RouterLink>
            </div>
          </div>
          
          <!-- Terminal -->
          <div class="relative">
            <div class="bg-gray-800 rounded-lg shadow-2xl">
              <!-- Terminal Header -->
              <div class="flex items-center px-4 py-2 bg-gray-700 rounded-t-lg">
                <div class="flex space-x-2">
                  <div class="w-3 h-3 rounded-full bg-red-500"></div>
                  <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div class="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div class="ml-4 text-sm text-gray-400">syniac-cli</div>
              </div>
              <!-- Terminal Content -->
              <div class="p-4 font-mono text-sm">
                <div v-for="(line, index) in terminalLines" :key="index" class="text-gray-300">
                  <span class="text-primary-400">$</span> {{ line }}
                </div>
                <div v-if="currentLine" class="text-gray-300">
                  <span class="text-primary-400">$</span> {{ currentLine }}
                  <span class="animate-pulse">|</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-12 bg-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="stat in stats" :key="stat.label" class="text-center">
            <div class="text-4xl font-bold text-primary-400">{{ stat.value }}</div>
            <div class="mt-2 text-gray-400">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-12 bg-gray-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h2 class="text-3xl font-extrabold text-white sm:text-4xl">
            Why Choose Us
          </h2>
          <p class="mt-4 max-w-2xl mx-auto text-xl text-gray-400">
            Experience the difference with our enterprise-grade cloud solutions
          </p>
        </div>

        <div class="mt-10">
          <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div
              v-for="feature in features"
              :key="feature.title"
              class="pt-6"
            >
              <div class="flow-root bg-gray-800 rounded-lg px-6 pb-8">
                <div class="-mt-6">
                  <div class="inline-flex items-center justify-center p-3 bg-primary-500 rounded-md shadow-lg">
                    <i :class="feature.icon" class="text-2xl text-white"></i>
                  </div>
                  <h3 class="mt-8 text-lg font-medium text-white tracking-tight">{{ feature.title }}</h3>
                  <p class="mt-5 text-base text-gray-400">
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Performance Metrics -->
    <section class="py-12 bg-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-8">
          <h2 class="text-3xl font-extrabold text-white">Performance Comparison</h2>
          <p class="mt-4 text-xl text-gray-400">See how we stack up against traditional solutions</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-gray-900 rounded-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-white">Response Time</h3>
              <span class="text-2xl font-bold text-primary-400">52% Faster</span>
            </div>
            <p class="text-gray-400">Average response time of 17ms compared to industry standard 36ms</p>
          </div>
          <div class="bg-gray-900 rounded-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-white">Resource Efficiency</h3>
              <span class="text-2xl font-bold text-primary-400">53% Better</span>
            </div>
            <p class="text-gray-400">Optimized resource utilization with advanced workload management</p>
          </div>
          <div class="bg-gray-900 rounded-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-white">Cost Savings</h3>
              <span class="text-2xl font-bold text-primary-400">65% Lower</span>
            </div>
            <p class="text-gray-400">Significant cost reduction through efficient resource allocation</p>
          </div>
        </div>
        <div class="mt-8 bg-gray-900 rounded-lg p-6">
          <div class="h-80">
            <Line :data="performanceData" :options="chartOptions" />
          </div>
        </div>
      </div>
    </section>

    <!-- Technology Stack -->
    <section class="py-12 bg-gray-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-extrabold text-white">Our Technology Stack</h2>
          <p class="mt-4 text-xl text-gray-400">Built on cutting-edge infrastructure and advanced technologies</p>
        </div>

        <div class="grid gap-8 lg:grid-cols-3">
          <div v-for="category in techStack" :key="category.category" class="space-y-6">
            <h3 class="text-2xl font-bold text-white text-center">{{ category.category }}</h3>
            <div class="space-y-4">
              <div v-for="item in category.items" :key="item.name"
                class="bg-gray-800 rounded-lg p-6 transform hover:scale-105 transition-all duration-300 hover:shadow-glow"
              >
                <div class="flex items-center space-x-4">
                  <div class="flex-shrink-0">
                    <i :class="[item.icon, 'text-3xl text-primary-400']"></i>
                  </div>
                  <div>
                    <h4 class="text-lg font-semibold text-white">{{ item.name }}</h4>
                    <p class="text-gray-400">{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
